<template>
  <v-app-bar color="secondary" density="compact" :flat="true">
    <v-toolbar-title class="text-button">{{ icdPresentationString }}: {{ icdDescription }} - {{ selected_menu_item.title }}</v-toolbar-title>
    <v-btn variant="text" icon="mdi-filter"
           @click.stop="filter_drawer = !filter_drawer"
           :disabled="dashboard_1.visible || dashboard_3.visible || dashboard_4.visible || dashboard_6.visible"></v-btn>
    <v-btn variant="text" icon="mdi-information"
           @click.stop="information_drawer = !information_drawer"
           :disabled="dashboard_1.visible || dashboard_6.visible"></v-btn>

    <template v-slot:extension>
      <v-tabs v-model="selected_menu_item" align-tabs="title" show-arrows>
        <v-tab v-for="menu_item in menu_items" :key="menu_item.dashboard" :value="menu_item"
               :disabled="menu_item.accessRight && !isAuthorized(menu_item.accessRight)">
          {{ menu_item.title }}
        </v-tab>
      </v-tabs>
    </template>
  </v-app-bar>

  <v-window v-model="selected_menu_item" direction="horizontal">
    <v-window-item :key="dashboard_1.dashboard" :value="dashboard_1" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_1.visible = event.value">
      <NeurologicalDiseaseProjectOverview :icd="icd"
                                          :disease-description="diseaseDescription"
                                          :project-description="resolvedProjectDescription"
                                          :visible="this.dashboard_1.visible"
                                          v-if="!this.initializing"></NeurologicalDiseaseProjectOverview>
    </v-window-item>
    <v-window-item :key="dashboard_2.dashboard" :value="dashboard_2" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_2.visible = event.value">
      <NeurologicalDiseaseDemographicBaseline :icd="icd"
                                              :disease-description="diseaseDescription"
                                              :age-at-diagnosis="this.selected_age_at_diagnosis"
                                              :sex="this.selected_sex"
                                              :diagnosis-date="this.selected_diagnosis_date"
                                              :survival-time-in-years="selectedSurvivalTimeInYears"
                                              :visible="this.dashboard_2.visible"
                                              v-on:diagnosisYearSelected="toggleAllOneYears($event)"
                                              v-on:ageGroupSelected="toggleAllOneAgeGroups($event)"
                                              v-on:sexSelected="toggleAllOneSexes($event)"
                                              v-if="!this.initializing"></NeurologicalDiseaseDemographicBaseline>
    </v-window-item>
    <v-window-item :key="dashboard_3.dashboard" :value="dashboard_3" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_3.visible = event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <NeurologicalDiseaseTreatmentPatternPrescribedDrugs :icd="icd"
                                                          :visible="this.dashboard_3.visible" v-if="!this.initializing"/>
    </v-window-item>
    <v-window-item :key="dashboard_4.dashboard" :value="dashboard_4" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_4.visible = event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <NeurologicalDiseaseTreatmentPaths :icd="icd"
                                         :visible="this.dashboard_4.visible" v-if="!this.initializing"></NeurologicalDiseaseTreatmentPaths>
    </v-window-item>
    <v-window-item :key="dashboard_5.dashboard" :value="dashboard_5" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_5.visible = event.value">
      <NeurologicalDiseaseOverallSurvival :icd="icd"
                                          :age-at-diagnosis="this.selected_age_at_diagnosis"
                                          :sex="this.selected_sex"
                                          :diagnosis-date="this.selected_diagnosis_date"
                                          :survival-time-in-years="selectedSurvivalTimeInYears"
                                          :visible="this.dashboard_5.visible" v-if="!this.initializing"></NeurologicalDiseaseOverallSurvival>
    </v-window-item>
    <v-window-item :key="dashboard_6.dashboard" :value="dashboard_6" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_6.visible = event.value;
                     this.filter_drawer &&= !event.value;
                     this.information_drawer &&= !event.value;
                   }">
      <NeurologicalDiseaseTreatmentPatternInterventions :icd="icd"
                                                        :visible="this.dashboard_6.visible" v-if="!this.initializing"></NeurologicalDiseaseTreatmentPatternInterventions>
    </v-window-item>
  </v-window>

  <v-navigation-drawer v-model="information_drawer"
                       v-bind:width="500"
                       :disable-resize-watcher="true"
                       :disable-route-watcher="true"
                       location="right"
                       temporary>
    <v-list density="compact" nav>
      <v-list-item prepend-icon="mdi-information" title="Information about the data set" nav></v-list-item>
    </v-list>

    <v-divider></v-divider>

    <v-list density="compact" nav>
      <v-list-item><strong>Data Source</strong></v-list-item>
      <v-list-item v-for="line in dataSourceInfoLines" :key="line" prepend-icon="mdi:mdi-minus">
        {{ line }}
      </v-list-item>
      <v-list-item><strong>Statistical Analysis</strong></v-list-item>
      <v-list-item v-for="line in statisticalAnalysisInfoLines" :key="line" prepend-icon="mdi:mdi-minus">
        {{ line }}
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-navigation-drawer v-model="filter_drawer"
                       v-bind:width="500"
                       :disable-resize-watcher="true"
                       :disable-route-watcher="true"
                       location="right">
    <v-card class="mx-auto epix-filter" elevation="0" ref="filters">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_age_at_diagnosis" :items="select_age_at_diagnosis_items"
                              label="Age at first diagnosis" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneAgeGroups">All/None</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_sex" :items="select_sex_items"
                              label="Sex" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneSexes">All/None</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['filters'].$el" :document-title="icd + '-' + 'filters'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import NeurologicalDiseaseProjectOverview from '@/components/NeurologicalDiseaseProjectOverview.vue'
import NeurologicalDiseaseDemographicBaseline from '@/components/NeurologicalDiseaseDemographicBaseline.vue'
import NeurologicalDiseaseOverallSurvival from '@/components/NeurologicalDiseaseOverallSurvival.vue'
import NeurologicalDiseaseTreatmentPatternInterventions from "@/components/NeurologicalDiseaseTreatmentPatternInterventions.vue";
import NeurologicalDiseaseTreatmentPatternPrescribedDrugs from "@/components/NeurologicalDiseaseTreatmentPatternPrescribedDrugs.vue";
import NeurologicalDiseaseTreatmentPaths from "@/components/NeurologicalDiseaseTreatmentPaths.vue";
import { VueToPrint } from "vue-to-print";

const DASHBOARD_1 = {
  title: 'Project Overview',
  dashboard: 'Dashboard1',
  visible: true
};

const DASHBOARD_2 = {
  title: 'Epidemiology and Baseline Characteristics',
  dashboard: 'Dashboard2',
  visible: false
};

const DASHBOARD_3 = {
  title: 'Treatment',
  dashboard: 'Dashboard3',
  visible: false
};

const DASHBOARD_4 = {
  title: 'Treatment Sequence',
  dashboard: 'Dashboard4',
  visible: false
};

const DASHBOARD_5 = {
  title: 'Overall Survival',
  dashboard: 'Dashboard5',
  visible: false
};

const DASHBOARD_6 = {
  title: 'Healthcare Resource Utilization',
  dashboard: 'Dashboard6',
  visible: false
};

export default defineComponent({
  name: 'NeurologicalDiseaseView',
  props: {
    icd: String,
    icdPresentationString: String,
    icdDescription: String,
    diseaseDescription: String,
    projectDescription: String,
    meshIds: Array,
    meshDescription: String,
    dashboard1Included: {
      type: Boolean,
      default: true
    },
    dashboard2Included: {
      type: Boolean,
      default: true
    },
    dashboard3Included: {
      type: Boolean,
      default: true
    },
    dashboard4Included: {
      type: Boolean,
      default: true
    },
    dashboard5Included: {
      type: Boolean,
      default: true
    },
    dashboard6Included: {
      type: Boolean,
      default: true
    }
  },
  components: {
    NeurologicalDiseaseProjectOverview,
    NeurologicalDiseaseDemographicBaseline,
    NeurologicalDiseaseOverallSurvival,
    NeurologicalDiseaseTreatmentPatternInterventions,
    NeurologicalDiseaseTreatmentPatternPrescribedDrugs,
    NeurologicalDiseaseTreatmentPaths,
    VueToPrint
  },
  computed: {
    resolvedProjectDescription() {
      return this.projectDescription || `<p>This project presents patient-level evidence for ${this.diseaseDescription}
      (${this.icdPresentationString}) across epidemiology, treatment, survival, and healthcare resource utilization.
      Methods text will be updated as final study documentation is confirmed.</p>`
    },
    selectedSurvivalTimeInYears() {
      return this.selected_survival_time_in_years.map(f => f.value)
    }
  },
  data: context => ({
    initializing: true,
    dashboard_1: DASHBOARD_1,
    dashboard_2: DASHBOARD_2,
    dashboard_3: DASHBOARD_3,
    dashboard_4: DASHBOARD_4,
    dashboard_5: DASHBOARD_5,
    dashboard_6: DASHBOARD_6,
    selected_menu_item: DASHBOARD_1,
    select_age_at_diagnosis_items: [],
    selected_age_at_diagnosis: [],
    select_sex_items: [],
    selected_sex: [],
    select_diagnosis_date_items: [],
    selected_diagnosis_date: [],
    select_survival_time_in_years_items: [],
    selected_survival_time_in_years: [],
    dataSourceInfoLines: [
      'National Patient Register (NPR): It collects detailed information on all inpatient and outpatient visits to specialized care, including admission and discharge dates, as well as diagnosis (ICD-10-SE code), medication (ATC code) and procedures (KVÅ code) associated with each visit.',
      'Prescribed Drug Register (PDR): It contains data on all prescribed drugs dispensed by all pharmacies in Sweden since July 2005. Data collected include the type of dispensed drug (ATC code), and the date, number, and size of the dispensation.',
      'Cause of Death Register (CDR): It registers information about death of Swedish residents, including date of death and the cause of death (ICD-10-SE code).',
      'Patients with MG (ICD-10-SE: G70.0) were identified in the NPR between 2000 and 2024.',
      'Data was then linked with the PDR and CDR to obtain information about treatment and mortality.',
      'For this Statistical Order, data extraction, linkage, anonymization, and aggregation were carried out by the Swedish National Board of Health and Welfare before being delivered to Ciencia Research for data visualization.'
    ],
    statisticalAnalysisInfoLines: [
      'All patients were identified between 1 Jan 2000 and 31 Dec 2024, and were followed up until 31 Dec 2024.',
      'Age at diagnosis was defined as the age of the earliest diagnosis record registered at the National Patient Register between 1 Jan 2000 and 31 Dec 20, hence any diagnosis of a patient made prior to the study period or his/her immigration to Sweden or in primary care could not be identified.',
      'In this Statistical Order, treatment data was further aggregated by the Swedish Board and Health and Welfare owing to data privacy considerations prior to delivery to Ciencia Research.',
      'Data on treatment date was only available as the number of days since index date rounded to the nearest 10 days.',
      'Using the available data, a Sankey diagram was used to illustrate treatment sequences among MG patients. Treatment sequence was determined by the approximated treatment start date, defined as either the dispensing date (for outpatient treatments) or the initiation date (for inpatient treatments). Due to the lack of treatment duration information, it was not possible to determine treatment discontinuation. Treatment initiated on the same approximated start date were considered combination therapy and are presented as Drug A + Drug B in the diagram.',
      'For healthcare resource utilization,',
      'A care visit due to MG is defined as an outpatient or inpatient care visit record that was associated with ICD-10-SE: G70.0 as the primary or secondary diagnosis.',
      'When calculating time spent in care, each outpatient visit is counted as 1 day. For inpatient care, if the patient had one overnight stay, the visit is counted as 2 days.',
      'Values less than 5 were censored as 1-4 by data providers to prevent potentially identifiable personal information in accordance with GDPR and the national data privacy regulations. Other values that may reveal these 1-4 values were also censored. For the same reason, data providers could not provide data if the sample size was deemed too small. To obtain data to the extent possible, some parameters (e.g., calendar, period, age, time in care, treatment type) were grouped to form larger sample size to allow data extraction.'
    ],
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      }
    },
    information_drawer: false,
    filter_drawer: false,
    menu_items: [
      context.dashboard1Included ? DASHBOARD_1 : null,
      context.dashboard2Included ? DASHBOARD_2 : null,
      context.dashboard3Included ? DASHBOARD_3 : null,
      context.dashboard4Included ? DASHBOARD_4 : null,
      context.dashboard5Included ? DASHBOARD_5 : null,
      context.dashboard6Included ? DASHBOARD_6 : null,
    ].filter(v => v !== null)
  }),
  methods: {
    isAuthorized(accessRight) {
      return this.$store.getters.stateUser?.access_rights?.includes(accessRight)
    },
    toggleAllNoneAgeGroups() {
      if (this.selected_age_at_diagnosis.length === this.select_age_at_diagnosis_items.length) {
        this.selected_age_at_diagnosis = [];
      } else {
        this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items]
      }
    },
    toggleAllOneAgeGroups(ageGroup) {
      if ((this.selected_age_at_diagnosis.length === 1) && (this.selected_age_at_diagnosis[0] === ageGroup)) {
        this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items]
      } else {
        this.selected_age_at_diagnosis = [ageGroup]
      }
    },
    toggleAllNoneSexes() {
      if (this.selected_sex.length === this.select_sex_items.length) {
        this.selected_sex = [];
      } else {
        this.selected_sex = [...this.select_sex_items]
      }
    },
    toggleAllOneSexes(sex) {
      if ((this.selected_sex.length === 1) && (this.selected_sex[0] === sex)) {
        this.selected_sex = [...this.select_sex_items]
      } else {
        this.selected_sex = [sex]
      }
    },
    toggleAllOneYears(year) {
      if ((this.selected_diagnosis_date.length === 1) && (this.selected_diagnosis_date[0] === year)) {
        this.selected_diagnosis_date = [...this.select_diagnosis_date_items]
      } else {
        this.selected_diagnosis_date = [year]
      }
    },
    async loadAgeAtDiagnosis() {
      return axios.get('/neurological-disease/ages-at-diagnosis',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadSexes() {
      return axios.get('/neurological-disease/sexes',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadDiagnosisYears() {
      return axios.get('/neurological-disease/diagnosis-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadSurvivalTimeInYears() {
      return axios.get('/neurological-disease/survival-times-in-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadStaticData() {
      return await Promise.all([
        this.loadAgeAtDiagnosis(),
        this.loadSexes(),
        this.loadDiagnosisYears(),
        this.loadSurvivalTimeInYears()
      ]).then((response) => {
        this.select_age_at_diagnosis_items = [...response[0].data];
        this.selected_age_at_diagnosis = [...response[0].data];
        this.select_sex_items = [...response[1].data];
        this.selected_sex = [...response[1].data];
        this.select_diagnosis_date_items = [...response[2].data];
        this.selected_diagnosis_date = [...response[2].data];
        this.select_survival_time_in_years_items = [...response[3].data];
        this.selected_survival_time_in_years = [...response[3].data];
        console.log('Static data loaded: NeurologicalDiseaseView')
      })
    }
  },
  watch: {
    icd() {
      this.loadStaticData()
    }
  },
  async mounted() {
    this.initializing = true
    await this.loadStaticData().then(() => {
      this.initializing = false
    }).finally(() => {
      this.initializing = false
    })
  }
})
</script>
